import { isStorageAttachment, isUploadedAttachment, Meeting, StorageAttachmentMeta, TaskAttachment } from "@/schema"
import { ReadonlyAttribute } from "@ui/forms/inline"
import { Paperclip, Plus, X } from "lucide-react"
import { toast } from "sonner"

import { useUpdateMeetingWithFiles } from "@/hooks/api/meetings"
import { useDeleteStorageAttachment } from "@/hooks/api/storage_attachments"
import { ATTACHMENT_MAX_FILES, AttachmentAddMenu } from "@/components/storage/attachment-add-menu"
import { StorageAttachmentChip } from "@/components/storage/storage-attachment-chip"

interface MeetingAttachmentsProps {
  meeting: Meeting
}

export default function MeetingAttachments({ meeting }: MeetingAttachmentsProps) {
  const { mutate: updateMeeting, isPending } = useUpdateMeetingWithFiles()
  const { mutate: deleteStorageLink } = useDeleteStorageAttachment()

  const attachments = meeting.attachments || []
  const uploaded = attachments.filter(isUploadedAttachment)
  const storageLinks = attachments.filter(isStorageAttachment)
  const canAddMore = attachments.length < ATTACHMENT_MAX_FILES

  const handleFilesSelected = (files: File[]) => {
    const existingSignedIds = uploaded.map((a) => a.signed_id)

    updateMeeting(
      { id: meeting.id, files, existingSignedIds },
      {
        onSuccess: () => {
          toast.success("File(s) uploaded successfully")
        },
      }
    )
  }

  const handleRemove = (attachmentToRemove: TaskAttachment, e: React.MouseEvent) => {
    e.stopPropagation()
    const remainingSignedIds = uploaded
      .filter((a) => a.signed_id !== attachmentToRemove.signed_id)
      .map((a) => a.signed_id)

    updateMeeting(
      { id: meeting.id, files: [], existingSignedIds: remainingSignedIds },
      {
        onSuccess: () => {
          toast.success("File removed")
        },
      }
    )
  }

  const handleRemoveStorageLink = (attachment: StorageAttachmentMeta) => {
    deleteStorageLink(
      { id: attachment.id, recordType: "Meeting" },
      {
        onSuccess: () => {
          toast.success("Link removed")
        },
      }
    )
  }

  return (
    <ReadonlyAttribute
      label="Attachments"
      LabelIcon={Paperclip}
      hideLabel
      className="items-start [&>*:first-child]:mt-1"
      renderValue={() => (
        <div className="flex w-full flex-col gap-2">
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {uploaded.map((attachment) => (
                <div key={attachment.signed_id} className="flex h-7 items-center gap-1 rounded-md border px-2 text-xs">
                  <Paperclip className="size-3" />
                  <a
                    href={attachment.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="max-w-[100px] truncate hover:underline"
                  >
                    {attachment.file_name}
                  </a>
                  <button
                    type="button"
                    onClick={(e) => handleRemove(attachment, e)}
                    className="text-icon hover:text-destructive ml-0.5 rounded-sm"
                  >
                    <X className="size-3" />
                  </button>
                </div>
              ))}
              {storageLinks.map((attachment) => (
                <StorageAttachmentChip key={attachment.id} attachment={attachment} onRemove={handleRemoveStorageLink} />
              ))}
            </div>
          )}

          {canAddMore && (
            <AttachmentAddMenu
              onFilesSelected={handleFilesSelected}
              existingCount={attachments.length}
              storageLink={{ recordType: "Meeting", recordId: meeting.id }}
              disabled={isPending}
            >
              <button
                type="button"
                disabled={isPending}
                className="text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-primary flex h-8 w-fit cursor-pointer items-center gap-1.5 rounded-md px-2 text-sm focus:outline-1 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Plus className="size-3.5" />
                <span>{attachments.length === 0 ? "Add attachment" : "Add more"}</span>
              </button>
            </AttachmentAddMenu>
          )}
        </div>
      )}
    />
  )
}
